/** Shared field defs for Airtable-style grid filters. Row shapes match each grid's mapped rows. */

const text = (id, label, getValue) => ({
    id,
    label,
    type: "text",
    ...(getValue ? { getValue } : {}),
});

const number = (id, label, getValue) => ({
    id,
    label,
    type: "number",
    ...(getValue ? { getValue } : {}),
});

const date = (id, label, getValue) => ({
    id,
    label,
    type: "date",
    ...(getValue ? { getValue } : {}),
});

const select = (id, label, options, getValue) => ({
    id,
    label,
    type: "select",
    options: options || [],
    ...(getValue ? { getValue } : {}),
});

export function PRODUCT_FILTER_FIELDS(meta = {}) {
    const choices = (key) => (meta[key]?.choices || []).filter(Boolean);
    return [
        text("itemName", "Item Name"),
        text("listingField", "Listing Field"),
        text("internalSkuId", "Internal SKU ID"),
        text("brand", "Brand"),
        text("azCategory", "AZ Category"),
        text("ebayCategory", "eBay Category"),
        select("conditionGrade", "Condition Grade", choices("conditionGrade")),
        text("statusChecks", "Status Checks"),
        text("asin", "ASIN"),
        text("upc", "UPC"),
        select("walmartBlocked", "Walmart Blocked?", choices("walmartBlocked")),
        text("listedPlatformsText", "Listed On", (row) =>
            Array.isArray(row.listedPlatforms) ? row.listedPlatforms.join(", ") : row.listedPlatformsText || ""
        ),
        number("vistaMaxPrice", "Vista Max Price"),
        number("macMaxPrice", "MAC Max Price"),
        number("bidftaMaxPrice", "BidFTA Max Price"),
        number("msrpUsd", "MSRP"),
        number("totalQuantity", "Total Quantity"),
        number("ebayOlderListingAge", "eBay Older Listing Age"),
        number("walmartOlderListingAge", "WM Older Listing Age"),
    ];
}

export const BUYER_VIEW_FILTER_FIELDS = [
    text("sku", "SKU"),
    text("itemName", "Product Name"),
    number("vistaMaxPrice", "Vista Max Price"),
    number("macMaxPrice", "MAC Max Price"),
    number("bidftaMaxPrice", "BidFTA Max Price"),
    number("triangleAndersonPrice", "Triangle Anderson Price"),
    number("triangleRaleighPrice", "Triangle Raleigh Price"),
    text("statusChecks", "Status Checks", (row) =>
        Array.isArray(row.statusChecks) ? row.statusChecks.join(", ") : row.statusChecks || ""
    ),
];

export const INVENTORY_LEVELS_FILTER_FIELDS = [
    text("itemName", "Product Name"),
    text("listingField", "Listing Field"),
    text("internalSkuId", "Internal SKU"),
    number("totalQuantOnHand", "Total"),
    number("availableQuantityWalmart", "Walmart"),
    number("availableQuantityEbay", "eBay"),
];

export function CATALOG_ITEM_FILTER_FIELDS(meta = {}) {
    const choices = (key) => (meta[key]?.choices || []).filter(Boolean);
    return [
        text("itemId", "Item ID"),
        text("sku", "SKU"),
        text("invoiceNumber", "Invoice Number"),
        text("shelves", "Shelf"),
        text("shelfBinName", "Bin"),
        select("platform", "Platform", choices("platform")),
        number("macSubId", "MAC Sub ID"),
        select("status", "Status", choices("status")),
        text("listPlatformText", "List Platform", (row) =>
            Array.isArray(row.listPlatform)
                ? row.listPlatform.filter(Boolean).join(", ")
                : row.listPlatformText || ""
        ),
        select("productGrade", "Grade", choices("productGrade")),
        date("purchaseDate", "Purchase Date", (row) => row.purchaseDateRaw || row.purchaseDate),
        date("dateListed", "Date Listed", (row) => row.dateListedRaw || row.dateListed),
        number("baseCost", "Base Cost"),
        select("needsLabel", "Needs Label", ["Yes", "No"], (row) =>
            row.needsLabel ? "Yes" : "No"
        ),
        text("notes", "Notes"),
    ];
}

export const NEEDS_LABEL_FILTER_FIELDS = [
    text("parserItemId", "Parser Item ID"),
    text("platform", "Platform"),
    text("itemName", "Item Name"),
    date("dateUploaded", "Uploaded", (row) => row.dateUploadedRaw || row.dateUploaded),
];

export function INVOICE_FILTER_FIELDS(meta = {}) {
    const choices = (key) => (meta[key]?.choices || []).filter(Boolean);
    return [
        text("invoiceNumber", "Invoice Number"),
        text("supplierName", "Supplier"),
        select("deliveryStatus", "Delivery Status", choices("deliveryStatus")),
        text("trackingId", "Tracking ID"),
        date("invoiceDate", "Invoice Date", (row) => row.raw?.invoiceDate || row.invoiceDate),
        date("receivedDate", "Received Date", (row) =>
            row.raw?.receivedDate || row.receivedDate
        ),
        date("uploadedDate", "Uploaded Date", (row) =>
            row.uploadedDateRaw || row.raw?.createdAt || row.uploadedDate
        ),
        number("daysSinceOrdered", "Days since ordered"),
        number("numberOfItemsOnInvoice", "Items on Invoice"),
        number("totalInvoiceAmount", "Total Amount"),
        text("lineItemStatuses", "Line Item Statuses"),
    ];
}

export const SHIPMENT_FILTER_FIELDS = [
    text("trackingNumber", "Tracking"),
    text("customerOrderNumber", "Order"),
    text("itemId", "Item ID"),
    select("orderChannel", "Channel", ["eBay", "Walmart"]),
    select("pickReady", "Ready to ship", ["Ready", "Not ready"]),
    select("status", "Status", ["Needs Label", "Shipped"]),
    select("shippedVia", "Shipped via", ["Bread Bskt", "eBay", "Walmart"]),
    text("buyerName", "Buyer"),
    text("shippingService", "Service"),
    number("labelCost", "Label cost"),
    number("quantity", "Qty"),
    text("shipCity", "City"),
    text("shipState", "State"),
    text("shipZip", "Zip"),
    date("dateCreated", "Created", (row) => row.raw?.airtableCreatedAt || row.raw?.createdAt || row.dateCreated),
];

export const RETURNS_FILTER_FIELDS = [
    text("rma", "RMA"),
    text("returnId", "Return ID"),
    text("orderNumber", "Order"),
    select("platform", "Platform", ["eBay", "Walmart"]),
    select("status", "Status", [
        "Pending Our Response",
        "Pending Customer Ship",
        "Pending ShipStation Response",
        "Pending UPS Response",
        "Pending Merchant Decision",
        "Non Refunded",
        "Refunded",
    ]),
    text("claimReason", "Claim Reason"),
    select("fileClaim", "File Claim", ["Not Needed", "Pending", "Filed", "Resolved"]),
    select("returnedItemStatus", "Returned Item Status", ["Pushed", "Not Pushed"]),
    text("skuName", "SKU"),
    text("itemName", "Item Name"),
    text("returnTrackingId", "Return Tracking"),
    text("disputeNumber", "Dispute Number"),
    number("quantity", "Quantity"),
    number("totalValue", "Total Value"),
    date("fileDate", "File Date", (row) => row.raw?.fileDate || row.fileDate),
];

export const SHELF_FILTER_FIELDS = [
    text("shelfName", "Shelf Name"),
    text("name", "Name"),
    text("binNames", "Bins"),
    number("binCount", "Bin count"),
    number("itemCount", "Item Count"),
    text("created", "Created"),
];

export const USER_FILTER_FIELDS = [
    text("email", "User Email"),
    text("firstName", "First Name"),
    text("lastName", "Last Name"),
];

/** Build filter fields from a DimensionGrid / Variants table spec. */
export function fieldsFromDimensionSpec(spec) {
    const columns = [...(spec?.fields || []), ...(spec?.readOnly || [])];
    return columns.map((field) => ({
        id: field.key,
        label: field.label,
        type: field.type === "number" ? "number" : "text",
    }));
}
